import { yupResolver } from '@hookform/resolvers/yup';
import { TextFieldProps } from '@mui/material/TextField/TextField';
import React from 'react';
import { useForm } from 'react-hook-form';
import {
  Box,
  Button,
  Icons,
  NumericInput,
  InputLabel,
  LinearProgress,
  Stack,
  Text,
  Input,
} from '@/theme/index';
import { schemaServiceItemForm } from '@/utils/schemaForms/schemaForms';
import {
  StyledServiceCard,
  ImageServiceContainer,
  StyledServiceImage,
  StyledText,
} from './components';
import { VehicleServiceCardUpdateModal } from '../modals/VehicleServiceCardUpdateModal';

interface MyGarageServiceCardProps {
  icon?: string;
  primaryText: string;
  children: React.ReactNode;
  height?: string;
  alertTag?: React.ReactNode;
}

export const MyGarageServiceCard: React.FC<MyGarageServiceCardProps> = ({
  icon,
  primaryText,
  children,
  height,
  alertTag,
}) => {
  return (
    <StyledServiceCard sx={{ height: height || '230px' }}>
      {alertTag}
      <ImageServiceContainer>
        <StyledServiceImage src={icon} />
      </ImageServiceContainer>
      <Text variant="h6" textAlign="center" color="primary.main">
        {primaryText}
      </Text>
      {children}
    </StyledServiceCard>
  );
};

interface InitialServiceCardProps {
  primaryText: string;
  label: string;
  inputType?: string;
  handleSubmitForm: (value: string) => void;
}
interface CustomizableInputProps {
  props: TextFieldProps;
  inputType: string;
}

const CustomizableInput = ({ props, inputType }: CustomizableInputProps) => {
  return inputType === 'date' ? (
    <Input
      type="date"
      {...props}
      inputProps={{
        // only needs the first 10 characters in the date string
        min: new Date().toISOString().slice(0, 10),
      }}
    />
  ) : (
    <NumericInput {...props} />
  );
};

export const InitialServiceCard: React.FC<InitialServiceCardProps> = ({
  primaryText,
  label,
  inputType = 'numeric',
  handleSubmitForm,
}) => {
  const { register, formState, getFieldState, getValues, handleSubmit, reset } =
    useForm<{
      serviceItemValue: string;
    }>({
      mode: 'onTouched',
      resolver: yupResolver(schemaServiceItemForm),
    });

  return (
    <>
      <Text variant="h6" textAlign="center">
        {primaryText}
      </Text>
      <Box
        component="form"
        onSubmit={handleSubmit(() => {
          handleSubmitForm(getValues('serviceItemValue'));
          reset();
        })}
      >
        <InputLabel
          htmlFor="serviceItemValue"
          sx={{
            fontSize: '.875rem',
            fontWeight: 'normal',
            color: 'grey.500',
            textAlign: 'center',
          }}
        >
          {label}
        </InputLabel>
        <Stack direction="row">
          <CustomizableInput
            props={{
              ...register('serviceItemValue'),
              name: 'serviceItemValue',
              variant: 'outlined',
              fullWidth: true,
              error: getFieldState('serviceItemValue').invalid,
            }}
            inputType={inputType}
          />
          <Button
            variant="contained"
            color="success"
            sx={{ ml: 0.5 }}
            type="submit"
            aria-label="Submit"
          >
            <Icons.ArrowForward />
          </Button>
        </Stack>
        {formState.errors.serviceItemValue?.message}
      </Box>
    </>
  );
};

interface InProgressServiceCardProps {
  primaryText: string;
  secondaryText: string;
  progress: number;
  serviceButton?: React.ReactNode;
  handleSubmit: (value: string) => void;
  serviceType: string;
}

export const InProgressServiceCard: React.FC<InProgressServiceCardProps> = ({
  primaryText,
  secondaryText,
  serviceButton,
  progress,
  handleSubmit,
  serviceType,
}) => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  return (
    <Stack direction="column" spacing={1}>
      <Text variant="h6" textAlign="center">
        {primaryText}
      </Text>
      <Box>
        <LinearProgress variant="determinate" value={progress} />
        <Text
          textAlign="center"
          sx={{ fontSize: '.75rem', color: 'grey.500', lineHeight: '166%' }}
        >
          {secondaryText}
        </Text>
      </Box>
      {serviceButton}
      <StyledText variant="body2" textAlign="center" onClick={handleOpenModal}>
        Mark as done
      </StyledText>
      <VehicleServiceCardUpdateModal
        serviceType={serviceType}
        isModalOpen={isModalOpen}
        closeModal={handleCloseModal}
        handleSubmitForm={handleSubmit}
      />
    </Stack>
  );
};

interface StaticServiceCardChildrenProps {
  text: string;
  handleClick: () => void;
}

export const StaticServiceCardChildren: React.FC<
  StaticServiceCardChildrenProps
> = ({ text, handleClick }) => {
  return (
    <>
      <Text variant="h6" textAlign="center">
        {text}
      </Text>
      <StyledText variant="body2" textAlign="center" onClick={handleClick}>
        See details
      </StyledText>
    </>
  );
};
